import React, { useEffect, useState } from "react";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";
import { css } from "@emotion/css";
import TextFieldRenderer from "../Form/TextFieldRenderer";
import { Button, IconButton, InputAdornment, MenuItem } from "@mui/material";
import { Clear as ClearIcon } from "@mui/icons-material";

const AutocompleteGeolocationStyles = css`
  .autocomplete-dropdown-container {
    border-bottom: honeydew;
    border-left: honeydew;
    border-right: honeydew;
    border-top: 1px solid #e6e6e6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    border-radius: 0 0 2px 2px;
    position: absolute;
    z-index: 10;
    width: 100%;
  }

  .suggestion-item {
    padding: 8px;
    text-align: left;
    background-color: #fff;
    cursor: pointer;
  }

  .suggestion-item--active {
    background-color: #fafafa;
  }

  .dropdown-footer {
    display: flex;
    justify-content: flex-end;
    padding: 4px;
  }

  .dropdown-footer-image {
    display: inline-block;
    width: 150px;
  }

  .spinner {
    color: #18bc9c;
    font-size: 30px;
  }

  .error-message {
    color: red;
  }

  .geocode-result-header {
    font-size: 20px;
    color: #222222;
  }
`;

const AutocompleteGeolocation = ({
  name,
  onChange,
  label,
  disabled,
  address = "",
  error,
  styles,
}) => {
  const [search, setSearch] = useState(address);
  const [errorMessage, setErrorMessage] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (address) {
      setSearch(address);
    }
  }, [address]);

  const onSearch = (address) => {
    setSearch(address);
    setErrorMessage(null); // Clear any previous error
  };

  const handleSelect = (suggestion) => {
    setErrorMessage(null); // Clear error message before new request
    geocodeByAddress(suggestion.description)
      .then((results) => {
        const selectedResult = results[0];
        const selectedAddress =
          suggestion.formattedSuggestion.mainText || suggestion.description;
        return getLatLng(selectedResult).then((latlng) => {
          const updatedLocation = {
            address: selectedAddress,
            ...latlng,
          };
          onChange(updatedLocation);
          setSearch(selectedAddress);
        });
      })
      .catch((error) => {
        console.error("Error on selecting address", error);
        setErrorMessage("An error occurred while fetching location data.");
      });
  };

  const retryFetchSuggestions = () => {
    if (retryCount < 3) {
      setRetryCount((prev) => prev + 1);
      setSearch(""); // Trigger re-fetch by resetting search
      setErrorMessage(null); // Clear error message
    } else {
      setErrorMessage("Failed to fetch data after multiple attempts.");
    }
  };

  const handleClear = () => {
    setSearch("");
    onChange(null); // Notify LocationRenderer to reset everything
  };

  return (
    <PlacesAutocomplete value={search} onChange={onSearch}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div
          className={AutocompleteGeolocationStyles}
          style={{ position: "relative" }}
        >
          <TextFieldRenderer
            name={name}
            styles={styles}
            disabled={disabled}
            label={label}
            autoComplete={getInputProps().autoComplete}
            style={{ marginBottom: "24px" }}
            onChange={getInputProps().onChange}
            error={error || errorMessage}
            isFromGeolocation={true}
            value={search}
            endAdornment={
              search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClear}
                    aria-label="clear address"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }
          />

          {errorMessage && (
            <div className="error-message">
              {errorMessage}
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={retryFetchSuggestions}
                style={{ marginLeft: "8px" }}
              >
                Retry
              </Button>
            </div>
          )}
          {suggestions.length > 0 && (
            <div className="autocomplete-dropdown-container">
              {loading && <div>Loading...</div>}
              {suggestions.map((suggestion) => {
                const { key, onClick, ...restProps } =
                  getSuggestionItemProps(suggestion); // Extract key and onClick separately
                const className = suggestion.active
                  ? "suggestion-item--active"
                  : "suggestion-item";
                const style = suggestion.active
                  ? { backgroundColor: "#f2f2f2", cursor: "pointer" }
                  : { backgroundColor: "#ffffff", cursor: "pointer" };

                return (
                  <div
                    key={key}
                    {...restProps}
                    className={className}
                    style={style}
                    onClick={() => {
                      handleSelect(suggestion);
                      if (onClick) onClick(); // Ensure existing onClick from getSuggestionItemProps is called
                    }}
                  >
                    <MenuItem>{suggestion.description}</MenuItem>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export default AutocompleteGeolocation;
